/**
 *
 * @module identityTokenStorage
 *
 */

import Check from 'check-types';
import Logging from './../logging/entry';
import Services from './../services/entry';

const Util = Services.Util;
const Storage = Services.Storage;
const IdentityToken = Services.Token.IdentityToken;

const InvalidArgumentException = Services.Exception.InvalidArgumentException;

const {
    instanceStrict,
    nonEmptyString
} = Check.assert;

/**
 *
 * @class IdentityTokenStorage
 * @desc Provides a storage mechanism for storing the IdentityToken.
 * @since 3.2.0
 *
 */
export default class IdentityTokenStorage {

    /**
     *
     * @param {String} clientId
     * @param {String} environment
     * @param {SDK.Logging.Logger} [logger=Logging]
     * @param {SDK.Services.Storage} [storage=null]
     * @throws {InvalidArgumentException}
     *
     */
    constructor(clientId, environment, logger = Logging, storage = null) {

        const errorMessage = {
            ctor: 'IdentityTokenStorage(clientId, environment, logger, storage)',
            get clientId() {
                return `${this.ctor} clientId should be a String with length`;
            },
            get environment() {
                return `${this.ctor} needs environment String`;
            },
            get storage() {
                return `${this.ctor} needs SDK.Services.Storage instance`;
            }
        };

        nonEmptyString(clientId, errorMessage.clientId, InvalidArgumentException);
        nonEmptyString(environment, errorMessage.environment, InvalidArgumentException);

        if (Check.assigned(storage)) {
            instanceStrict(storage, Storage, errorMessage.storage, InvalidArgumentException);
        }

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.clientId = clientId;

        /**
         *
         * @access private
         * @type {String}
         *
         */
        this.environment = environment;

        /**
         *
         * @access private
         * @type {SDK.Logging.Logger}
         *
         */
        this.logger = logger;

        /**
         *
         * @access private
         * @type {SDK.Services.Storage}
         *
         */
        this.storage = storage || new Storage(this.logger);

        /**
         *
         * @access private
         * @type {SDK.Services.Token.IdentityToken|Null}
         *
         */
        this.identityToken = null;

        /**
         *
         * @access private
         * @type {String}
         * @desc cache key scoped under client ID and environment to prevent clashes,
         * maintains the same structure as all other cacheKey(s) in the SDK
         *
         */
        this.cacheKey = `
            __bam_sdk_identity_bam_identityToken--${this.clientId}_${this.environment}
        `.replace(/(?:\n\s+)/g, '');

        this.logger.log(IdentityTokenStorage.name, 'Created.');

        /**
         *
         * @note attempt to fetch IdentityToken from the {SDK.Services.Storage} implementation
         *
         */
        this.getIdentityTokenFromStorage();

    }

    /**
     *
     * @access private
     * @param {SDK.Services.Token.IdentityToken} identityToken
     * @desc attempts to store an IdentityToken via the Storage API
     * @returns {Promise<Void>}
     *
     */
    saveIdentityToken(identityToken) {

        const errorMessage = {
            method: 'IdentityTokenStorage.saveIdentityToken(identityToken)',
            get identityToken() {
                return `${this.method} needs SDK.Services.Token.IdentityToken instance`;
            }
        };

        instanceStrict(identityToken, IdentityToken, errorMessage.identityToken, InvalidArgumentException);

        const { cacheKey, logger, storage } = this;

        logger.info(IdentityTokenStorage.name, `Save IdentityToken to Storage using key: "${cacheKey}".`);

        this.identityToken = identityToken;

        return storage.set(cacheKey, this.identityToken);

    }

    /**
     *
     * @access private
     * @desc returns an IdentityToken
     * @returns {SDK.Services.Token.IdentityToken}
     *
     */
    getIdentityToken() {

        const { logger } = this;

        logger.info(IdentityTokenStorage.name, 'Get cached IdentityToken.');

        return this.identityToken;

    }

    /**
     *
     * @access private
     * @desc deletes the current IdentityToken from Storage
     * @returns {Promise<Void>}
     *
     */
    clear() {

        const { cacheKey, logger, storage } = this;

        logger.warn(IdentityTokenStorage.name, `Clearing cached IdentityToken for key: "${cacheKey}".`);

        this.identityToken = null;

        return storage.remove(cacheKey);

    }

    /* #region private: */

    /**
     *
     * @access private
     * @desc executed a single time on IdentityTokenStorage instantiation
     * checks Storage to determine if IdentityToken was previously stored,
     * should fail silently if no IdentityToken found
     * @returns {Promise<SDK.Services.Token.IdentityToken>}
     *
     */
    getIdentityTokenFromStorage() {

        const { cacheKey, logger, storage } = this;

        logger.info(IdentityTokenStorage.name, `Get IdentityToken from Storage using cache key: "${cacheKey}".`);

        return storage.get(cacheKey).catch(() => {

            return Promise.resolve(this.identityToken);

        }).then((identityTokenFromStorage) => {

            const token = Util.getSafe(() => identityTokenFromStorage.token);
            const expiration = Util.getSafe(() => identityTokenFromStorage.expiration);

            if (Check.assigned(token)) {
                this.identityToken = new IdentityToken(token, expiration);
            }

            return Promise.resolve(this.identityToken);

        });

    }

}



// WEBPACK FOOTER //
// ./src/token/identityTokenStorage.js